import { motion, useMotionValue, useSpring } from "framer-motion";
import { profile, links } from "../data/profile";
import s from "./Contact.module.css";

const EASE = [0.16, 1, 0.3, 1] as const;

const panel = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.1 } },
};

const item = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: EASE } },
};

/** The primary action leans toward the pointer. Motion values only, no re-renders. */
function MagneticLink({ href, children }: { href: string; children: React.ReactNode }) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 180, damping: 18, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 180, damping: 18, mass: 0.4 });

  return (
    <motion.a
      href={href}
      className={`btn ${s.primary}`}
      style={{ x: sx, y: sy }}
      onPointerMove={(e: React.PointerEvent<HTMLAnchorElement>) => {
        const r = e.currentTarget.getBoundingClientRect();
        x.set((e.clientX - (r.left + r.width / 2)) * 0.3);
        y.set((e.clientY - (r.top + r.height / 2)) * 0.3);
      }}
      onPointerLeave={() => {
        x.set(0);
        y.set(0);
      }}
      whileTap={{ scale: 0.97 }}
    >
      {children}
    </motion.a>
  );
}

export default function Contact() {
  return (
    <section id="contact" className={`section ${s.contact}`} aria-labelledby="contact-title" tabIndex={-1}>
      <div className="container">
        <motion.div
          className={s.panel}
          variants={panel}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.h2 id="contact-title" className={s.title} variants={item}>
            Let&rsquo;s build something that ships.
          </motion.h2>
          <motion.p className={s.lede} variants={item}>
            Hiring, freelance, or a second opinion on an architecture. I reply to every message.
          </motion.p>
          <motion.div className={s.actions} variants={item}>
            <MagneticLink href={`mailto:${profile.email}`}>Email me</MagneticLink>
            <span className={s.address}>{profile.email}</span>
          </motion.div>
          <motion.ul className={s.profiles} variants={item} aria-label="Profiles">
            {links.map(({ title, href, icon: Icon }) => (
              <li key={title}>
                <a href={href} target="_blank" rel="noreferrer" className={s.profile}>
                  <Icon size={20} weight="fill" aria-hidden="true" />
                  {title}
                </a>
              </li>
            ))}
          </motion.ul>
        </motion.div>
      </div>
    </section>
  );
}
